import { ouvirAcesso, type AcessoNovo } from './acesso'
import { renovar } from './renovacao'

/** Folga máxima antes do `exp` (faixa 25 §3.2). */
export const FOLGA_MAXIMA_S = 60
/** 503/rede (§4): a primeira em segundos — cai dentro da graça de 30 s do back —, depois até 30 s. */
export const ESPERAS_DE_NOVA_TENTATIVA_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const
/** O relógio de parede é conferido de tempos em tempos: timer de aba em segundo plano e de máquina dormindo atrasa. */
export const INTERVALO_DO_VIGIA_MS = 30_000

/** `expires_in − min(60, expires_in / 2)` segundos, em ms. Nunca menos que `expires_in/2` (I3). */
export function atrasoDaRenovacaoMs(expiresIn: number): number {
  return (expiresIn - Math.min(FOLGA_MAXIMA_S, expiresIn / 2)) * 1000
}

/** Espera da n-ésima nova tentativa (0 = a primeira) depois de renovação indisponível. */
export function esperaDaTentativaMs(tentativa: number): number {
  const ultima = ESPERAS_DE_NOVA_TENTATIVA_MS.length - 1
  return ESPERAS_DE_NOVA_TENTATIVA_MS[Math.min(Math.max(tentativa, 0), ultima)]
}

export interface OpcoesDoAgendador {
  /** `sessao-morta`/`saida-pendente` numa aba aberta. O portão passa `saida.sairPorSessaoMorta`. */
  aoPerderSessao: () => void
}

/**
 * Renova sozinho antes do `exp` (faixa 25 §3.2–§3.4). Ver `agendador.md`. Devolve quem para tudo.
 */
export function iniciarAgendador({ aoPerderSessao }: OpcoesDoAgendador): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  /** Epoch ms de PAREDE do disparo agendado — o vigia e o "ao voltar" comparam com `Date.now()`. */
  let proximaEm: number | null = null
  let ultimoExpiresIn: number | null = null
  let tentativa = 0
  let parado = false

  const cancelar = () => {
    if (timer !== null) clearTimeout(timer)
    timer = null
    proximaEm = null
  }

  const agendarEm = (ms: number) => {
    cancelar()
    proximaEm = Date.now() + ms
    timer = setTimeout(() => void disparar(), ms)
  }

  async function disparar(): Promise<void> {
    cancelar()
    const resultado = await renovar()
    if (parado) return
    if (resultado.tipo === 'renovada') return // o access novo reagenda pelo ouvinte
    // Faixa 28 §4: o bloqueio está aceso (a renovação o acendeu). Não reagenda — seria um refresh recusado a cada volta —
    // e não perde a sessão: o cookie vale. O access da liberação reagenda pelo ouvinte.
    if (resultado.tipo === 'rede-bloqueada') return
    if (resultado.tipo === 'indisponivel') {
      agendarEm(esperaDaTentativaMs(tentativa))
      tentativa += 1
      return
    }
    aoPerderSessao()
  }

  const aoAcessoNovo = (novo: AcessoNovo) => {
    const anterior = ultimoExpiresIn
    ultimoExpiresIn = novo.expiresIn
    tentativa = 0
    // §3.3 teto: `expires_in` MENOR que o anterior = a sessão encostou no teto (`exp = min(agora+TTL, teto)`).
    // Não agenda mais e deixa o access vencer: o 401 seguinte renova até o back dizer REFRESH_INVALID.
    if (anterior !== null && novo.expiresIn < anterior) {
      cancelar()
      return
    }
    agendarEm(atrasoDaRenovacaoMs(novo.expiresIn))
  }

  const aoVoltar = () => {
    if (parado || proximaEm === null || Date.now() < proximaEm) return
    void disparar()
  }
  const aoMudarVisibilidade = () => {
    if (document.visibilityState === 'visible') aoVoltar()
  }

  const desligarOuvinte = ouvirAcesso(aoAcessoNovo)
  document.addEventListener('visibilitychange', aoMudarVisibilidade)
  window.addEventListener('online', aoVoltar)
  const vigia = setInterval(aoVoltar, INTERVALO_DO_VIGIA_MS)

  return () => {
    parado = true
    cancelar()
    desligarOuvinte()
    document.removeEventListener('visibilitychange', aoMudarVisibilidade)
    window.removeEventListener('online', aoVoltar)
    clearInterval(vigia)
  }
}
